import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { OptionalField, UploadDialogConfig, ValidatorService } from '@shared/models/fileupload';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-fileupload',
	templateUrl: './fileupload.component.html',
	styleUrls: ['./fileupload.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FileuploadComponent implements OnDestroy, OnInit, AfterViewInit {
	private unsubscribe: Subject<void> = new Subject<void>();
	public files: any[] = [];
	public fileName: string = '';
	public illegalFileFormat: string | boolean = false;
	public headingKey: string;
	public subHeadingKey: string;
	public allowedFileTypes: string[];
	public errorMessage: any;
	private validatorService: ValidatorService;
	public loadingHandler: Subject<boolean>;
	public optionalFields: OptionalField[] = [];
	public formData: Record<string, FormControl> = {};
	public allowedMimeTypes: string[]; // For "accept" parameter in <input type='file'>
	public mode: 'create' | 'edit' = 'create';
	public data: any;
	public processFileHook: () => boolean;
	public removeFileHook: () => boolean;
	public initHook: () => boolean;

	@Input() public confirmButtonKey: string = 'fileuploader.savebtn';
	@Input() public notificationHeader: string = 'fileuploader.file-uploaded-notification-header';

	@Input() public notificationContent: string = 'fileuploader.file-uploaded-notification-content';

	@Input() public notificationErrorHeader: string = 'fileuploader.file-not-uploaded-notification-header';

	@Input() public notificationErrorContent: string = 'fileuploader.file-not-uploaded-notification-content';

	@Input() public notificationClientErrorHeader: string = 'fileuploader.client-error-notification-header';

	@Input() public notificationClientErrorContent: string = 'fileuploader.client-error-notification-content';

	@Input() public detailedClientErrorMessage: string = 'fileuploader.detailed-client-error-message';

	// Note:
	// private injector: Injector // <-- could be used to inject the validator service here instead, see below
	// if (this.uploaderConfig.allowedFileTypes.includes('xls')) { this.validatorService = this.injector.get(BudgetvalidatorService); }

	constructor(
		public dialogRef: MatDialogRef<FileuploadComponent>,
		@Inject(MAT_DIALOG_DATA) public uploaderConfig: UploadDialogConfig,
		private notificationService: NotificationService,
		private translate: TranslateService,
		private injector: Injector,
	) {}

	ngAfterViewInit(): void {
		const elm = document.getElementById('subHeadingKey');

		if (elm) {
			setTimeout(() => {
				elm.scrollIntoView();
			}, 200);
		}
	}

	ngOnInit(): void {
		/*
        UploadDialogConfig har disse properties:
        headingKey: F.eks. 'insights.administration.reports.upload.heading',
        subHeadingKey: F.eks. 'insights.administration.reports.upload.subheading',
        allowedFileTypes: F.eks. ['.pbix'],
        validatorService: F.eks. this.injector.get(PowerbivalidatorService),
        loadingHandler: F.eks. this.loadingSubject,
        optionalFields: F.eks. [
          { name:'powerbi_report_title', titleKey: 'insights.administration.reports.upload.titlefield', mandatory: true },
        ]
    */
		Object.assign(this, this.uploaderConfig); // Dekonstruer config-objekt til klasseparametre

		if (typeof this.loadingHandler === 'undefined') {
			// Hvis der ikke er sendt et subject med til styring af en components egen loading-spinner,
			// viser/skjuler vi den her komponents loading-spinner i stedet
			this.loadingHandler = new Subject<boolean>();
		}

		if (this.optionalFields.length) {
			this.optionalFields.forEach((field) => {
				// if (field.validatorPattern) {
				this.formData[field.name] = new FormControl(
					{
						value: field.value ? field.value : '',
						disabled: field.readonly,
					}, // Edit existing value if present
					{
						validators: field.validatorPattern ? [Validators.pattern(field.validatorPattern)] : null,
					},
				);
				// } else {
				// }
			});
		}

		if (this.initHook && typeof this.initHook == 'function') {
			const changes = this.initHook();

			if (changes) {
				this.applyFormChanges();
			}
		}

		this.validatorService.statusSubject$.pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
			if (res.status === 'success') {
				if (this.mode === 'create') {
					// Vi gider ikke smide en notifikation, når man kun redigerer metadata på en eksisterende fil
					this.notificationService.notify([this.notificationHeader], [this.notificationContent, { filename: this.fileName }], 'success');
				}
				this.loadingHandler.next(false);
				this.dialogRef.close(res);
			}

			if (res.status === 'error') {
				this.loadingHandler.next(false);
				this.notificationService.notify([this.notificationErrorHeader], [this.notificationErrorContent, { filename: this.fileName }], 'error');
				this.errorMessage = this.translate.instant('fileuploader.detailed-error-message', {
					filename: this.fileName,
					error: res.data,
				});
				this.fileName = '';

				if (res.close) {
					this.dialogRef.close(res);
				}
			}

			if (res.status === 'client_error') {
				this.loadingHandler.next(false);
				this.notificationService.notify([this.notificationClientErrorHeader], [this.notificationClientErrorContent], 'error');
				this.errorMessage = this.translate.instant('fileuploader.detailed-client-error-message', { error: res.data });
				this.fileName = '';

				if (res.close) {
					this.dialogRef.close(res);
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	fileBrowseHandler(event): void {
		this.prepareFilesList(event.target.files);
		event.target.value = null; // To make re-selection of same file work
	}

	selectValueChange(): void {}

	onFileDropped(event: FileList): void {
		this.prepareFilesList(event);
	}

	prepareFilesList(files: Array<any> | FileList) {
		this.illegalFileFormat = false;
		this.fileName = '';
		this.errorMessage = '';
		this.files.splice(0, this.files.length);
		for (let index = 0; index < files.length; index++) {
			this.files.push(files[index]);
		}

		const extension = this.files[0].name.split('.').pop();

		if (!this.allowedFileTypes.includes(`.${extension}`)) {
			this.files.splice(0, this.files.length);
			this.illegalFileFormat = extension;
			return;
		} else {
			this.fileName = this.files[0]?.name;
		}

		if (this.processFileHook && typeof this.processFileHook == 'function') {
			const changes = this.processFileHook();

			if (changes) {
				this.applyFormChanges();
			}
		}
	}

	applyFormChanges() {
		this.optionalFields.forEach((field: OptionalField) => {
			if (!this.formData[field.name]) {
				this.formData[field.name] = new FormControl(
					{
						value: field.value ? field.value : '',
						disabled: field.readonly,
					},
					{
						validators: field.validatorPattern ? [Validators.pattern(field.validatorPattern)] : null,
					},
				);
			} else {
				if (!this.uploaderConfig.keepFormDataOnUpload || field.alwaysUpdate) {
					this.formData[field.name].setValue(field.value);
				}
				if (field.readonly) {
					this.formData[field.name].disable();
				} else {
					this.formData[field.name].enable();
				}
			}
		});
	}

	deleteFile(): void {
		this.fileName = '';
		this.files.splice(0, this.files.length);
		const changes = this.removeFileHook();

		if (changes) {
			this.applyFormChanges();
		}
	}

	processFile(): void {
		this.loadingHandler.next(true);
		this.validatorService.validate(this.files, this.formData, this.data);
	}

	checkRequiredFields() {
		if (!this.optionalFields.length) return true;

		let ok = true;
		this.optionalFields
			.filter((f) => f.mandatory === true)
			.forEach((field) => {
				if (field.type === 'number') {
					if (this.formData[field.name].getRawValue() == null) {
						ok = false;
						// this.formData[field.name].setValue('');
						this.formData[field.name].setErrors({
							incorrect: true,
						});
					}
				} else {
					if (!this.formData[field.name].getRawValue() || (field.type === 'number' && this.formData[field.name].getRawValue() == null)) {
						ok = false;
						// this.formData[field.name].setValue('');
						this.formData[field.name].setErrors({
							incorrect: true,
						});
					}
				}
			});

		return ok;
	}
}
